﻿import { useState, useRef, useMemo } from 'react';
import { addDays, parseISO, subDays } from 'date-fns';

function getTodayIST(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

// Sunday is the company weekly off — it can never be a leave start or end date.
const SUNDAY = 0;
const isSunday = (d: Date) => d.getDay() === SUNDAY;
// Upcoming blocked dates listed under the date pickers
const BLOCKED_HINT_COUNT = 3;
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, Loader2, X, FileText, UploadCloud, CalendarX2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { leaveService } from '@/services/leave.service';
import { useLeaveBlockedDates } from '@/hooks/leave/useLeaveBlockedDates';
import { formatDate } from '@/lib/dateFormat';
import type { LeaveRequest } from '@pms/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  request?: LeaveRequest;
}

export function ApplyLeaveModal({ open, onClose, request }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEdit = !!request;

  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(request?.leave_type_id ?? null);
  const [startDate, setStartDate] = useState(request?.start_date ?? '');
  const [endDate, setEndDate] = useState(request?.end_date ?? '');
  const [isHalfDay, setIsHalfDay] = useState(request?.is_half_day ?? false);
  const [halfDayPeriod, setHalfDayPeriod] = useState<'morning' | 'afternoon'>(request?.half_day_period ?? 'morning');
  const [reason, setReason] = useState(request?.reason ?? '');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const { data: types = [] } = useQuery({
    queryKey: ['leave', 'types'],
    queryFn: () => leaveService.listLeaveTypes(),
  });

  const { data: balances = [] } = useQuery({
    queryKey: ['leave', 'balances'],
    queryFn: () => leaveService.getMyBalances(),
  });

  const { data: rules = [] } = useQuery({
    queryKey: ['leave', 'rules'],
    queryFn: () => leaveService.listLeaveRules(),
  });

  // Dates HR blocked: no leave may start, end or pass over one
  const { blocked, isBlocked, firstBlockedIn, firstBlockedAfter } = useLeaveBlockedDates(open);
  const isOffDay = (d: Date) => isSunday(d) || isBlocked(d);

  const todayStr = useMemo(() => getTodayIST(), []);
  const today    = useMemo(() => parseISO(todayStr), [todayStr]);

  const selectedType    = types.find((t) => t.id === selectedTypeId) ?? null;
  const selectedBalance = balances.find((b) => b.leave_type_id === selectedTypeId) ?? null;
  const selectedRule    = rules.find((r) => r.leave_type_id === selectedTypeId) ?? null;

  // Sundays inside the range are weekly offs, so they never count as leave days.
  const calcDays = () => {
    if (!startDate || !endDate) return 0;
    if (isHalfDay) return 0.5;
    const end = parseISO(endDate);
    let days = 0;
    for (let d = parseISO(startDate); d <= end; d = addDays(d, 1)) {
      if (!isSunday(d)) days++;
    }
    return Math.max(1, days);
  };

  const daysRequested   = calcDays();
  // When editing, reclaim the days this request currently holds (same type only)
  // since they'll be released back once the edit replaces the old values.
  const reclaimedDays   = isEdit && request!.leave_type_id === selectedTypeId ? Number(request!.days_count) : 0;
  const available       = Number(selectedBalance?.available ?? 0) + reclaimedDays;
  // For paid leave: block when requested days exceed balance. No LOP allowed.
  const exceedsBalance  = (selectedType?.is_paid ?? false) && daysRequested > 0 && daysRequested > available;

  // Cap the end-date picker: paid leave cannot exceed available balance.
  // Sundays are skipped, so available=2.0 from Sat → end = the following Mon.
  const balanceMaxEndDate: Date | undefined = (() => {
    if (!selectedType?.is_paid || !startDate) return undefined;
    const maxDays = Math.floor(available);
    let end = parseISO(startDate);
    if (maxDays <= 0) return end; // no full days left → end = start (only half-day possible)
    let counted = isSunday(end) ? 0 : 1;
    while (counted < maxDays) {
      end = addDays(end, 1);
      if (!isSunday(end)) counted++;
    }
    return end;
  })();

  // The range must stop the day before the next blocked date after the start
  const nextBlocked = startDate ? firstBlockedAfter(startDate) : undefined;
  const blockedMaxEndDate = nextBlocked ? subDays(parseISO(nextBlocked.blocked_date), 1) : undefined;
  const maxEndDate =
    balanceMaxEndDate && blockedMaxEndDate
      ? (balanceMaxEndDate < blockedMaxEndDate ? balanceMaxEndDate : blockedMaxEndDate)
      : balanceMaxEndDate ?? blockedMaxEndDate;

  // An edit that keeps its dates stays allowed even if HR blocked them after it was applied
  const datesUnchanged = isEdit && startDate === request!.start_date && endDate === request!.end_date;
  const blockedInRange = startDate && endDate && !datesUnchanged ? firstBlockedIn(startDate, endDate) : undefined;

  const handleStartChange = (v: string) => {
    setStartDate(v);
    if (isHalfDay) setEndDate(v);
    else if (endDate && v && (endDate < v || firstBlockedIn(v, endDate))) setEndDate('');
  };

  const mutation = useMutation({
    mutationFn: async () => {
      // Upload a newly picked file first; an edit without a new file keeps the current one.
      const document_url = documentFile
        ? await leaveService.uploadLeaveDocument(documentFile)
        : request?.document_url ?? undefined;
      const payload = {
        leave_type_id: selectedTypeId!,
        start_date: startDate,
        end_date: endDate,
        is_half_day: isHalfDay,
        half_day_period: isHalfDay ? halfDayPeriod : undefined,
        reason,
        document_url,
      };
      return isEdit
        ? leaveService.updateLeaveRequest(request!.id, payload)
        : leaveService.createLeaveRequest(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Leave request updated successfully' : 'Leave request submitted successfully');
      qc.invalidateQueries({ queryKey: ['leave'] });
      handleClose();
    },
    onError: (err: { response?: { data?: { error?: { message?: string } } } }) => {
      const msg = err.response?.data?.error?.message
        ?? (isEdit ? 'Failed to update leave request' : 'Failed to submit leave request');
      toast.error(msg);
      if (msg.includes('already reviewed')) {
        qc.invalidateQueries({ queryKey: ['leave'] });
        handleClose();
      }
    },
  });

  const handleClose = () => {
    setSelectedTypeId(null);
    setStartDate('');
    setEndDate('');
    setIsHalfDay(false);
    setHalfDayPeriod('morning');
    setReason('');
    setDocumentFile(null);
    onClose();
  };

  function handleFileDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setDocumentFile(file);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) setDocumentFile(file);
  }

  const canSubmit =
    selectedTypeId &&
    startDate &&
    startDate >= todayStr &&
    endDate &&
    reason.trim().length >= 3 &&
    !mutation.isPending &&
    !exceedsBalance &&
    !blockedInRange;

  const totalUsed = balances.reduce((sum, b) => sum + Number(b.used), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden flex flex-col max-h-[90vh] gap-0">
        {/* Header */}
        <DialogHeader className="px-5 py-4 border-b border-border/60 text-left">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <span className="text-xl">✦</span> {isEdit ? 'Edit Leave Request' : 'Apply for Leave'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the details of your pending request' : 'Fill in the details to submit your leave request'}
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Leave Type Selection */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Select Leave Type
            </p>
            <div className="grid grid-cols-2 gap-2">
              {types.filter((t) => t.is_active).map((type) => {
                const bal = balances.find((b) => b.leave_type_id === type.id);
                const isSelected = type.id === selectedTypeId;
                return (
                  <button
                    key={type.id}
                    onClick={() => { setSelectedTypeId(type.id); setIsHalfDay(false); }}
                    className={`relative text-left rounded-xl border p-3 transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-border/80 hover:bg-muted/30'
                    }`}
                  >
                    {isSelected && (
                      <CheckCircle2 className="absolute top-2 right-2 h-4 w-4 text-primary" />
                    )}
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: type.color }} />
                      <span className="text-xs font-semibold leading-tight line-clamp-1">{type.name}</span>
                    </div>
                    {type.is_paid && bal ? (
                      <p className="text-[10px] text-muted-foreground">
                        {Number(bal.available).toFixed(1)} days left
                      </p>
                    ) : (
                      <p className="text-[10px] text-amber-600">Unpaid</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Balance Summary */}
          {selectedType && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-0.5">
                    Available
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    {selectedType.is_paid
                      ? `${Number(selectedBalance?.available ?? 0).toFixed(1)} days`
                      : '0 days'}
                  </p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>Entitled: {Number(selectedBalance?.accrued ?? 0).toFixed(1)}</p>
                  <p>Taken: {Number(selectedBalance?.used ?? 0).toFixed(1)}</p>
                </div>
              </div>
              <div className="border-t border-primary/10 pt-2 flex justify-between text-xs text-muted-foreground">
                <span>Total Taken <span className="font-semibold">{totalUsed.toFixed(1)} days</span></span>
              </div>
            </div>
          )}

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground mb-1.5 block">
                From Date <span className="text-destructive">*</span>
              </label>
              <DatePicker
                value={startDate}
                onChange={handleStartChange}
                minDate={today}
                disableDay={isOffDay}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground mb-1.5 block">
                To Date <span className="text-destructive">*</span>
              </label>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                disabled={isHalfDay}
                minDate={startDate ? parseISO(startDate) : today}
                maxDate={maxEndDate}
                disableDay={isOffDay}
              />
            </div>
          </div>

          {/* Blocked dates — greyed out in both calendars, listed here with HR's reason */}
          {blockedInRange ? (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
              <CalendarX2 className="h-4 w-4 text-destructive shrink-0" />
              <p className="text-xs text-destructive">
                <strong>{formatDate(blockedInRange.blocked_date)}</strong> is blocked for leave
                {blockedInRange.reason ? ` (${blockedInRange.reason})` : ''}. Choose dates that do not include it.
              </p>
            </div>
          ) : blocked.length > 0 && (
            <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
              <CalendarX2 className="h-3.5 w-3.5 mt-px shrink-0" />
              <p>
                Leave can't be applied on:{' '}
                {blocked.slice(0, BLOCKED_HINT_COUNT).map((b, i) => (
                  <span key={b.id}>
                    {i > 0 && ', '}
                    <span className="font-medium text-foreground">{formatDate(b.blocked_date)}</span>
                    {b.reason && ` (${b.reason})`}
                  </span>
                ))}
                {blocked.length > BLOCKED_HINT_COUNT && ` +${blocked.length - BLOCKED_HINT_COUNT} more`}
              </p>
            </div>
          )}

          {/* Half Day Toggle */}
          {selectedRule?.allow_half_day && (
            <div>
              <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/20">
                <div className="flex items-center gap-2">
                  <span>🌤</span>
                  <div>
                    <p className="text-sm font-medium">Half Day Leave</p>
                    <p className="text-xs text-muted-foreground">Apply for half day only</p>
                  </div>
                </div>
                <Switch
                  checked={isHalfDay}
                  onChange={(next) => {
                    setIsHalfDay(next);
                    // A half day is a single day, so switching it on collapses the range.
                    if (next && startDate) setEndDate(startDate);
                  }}
                  aria-label="Half Day Leave"
                />
              </div>
              {isHalfDay && (
                <div className="flex gap-2 mt-2">
                  {(['morning', 'afternoon'] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => setHalfDayPeriod(p)}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors capitalize ${
                        halfDayPeriod === p
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-border text-muted-foreground hover:bg-muted/40'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Insufficient balance — hard block for paid leave */}
          {exceedsBalance && (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
              <p className="text-xs text-destructive">
                Insufficient balance. You have <strong>{available.toFixed(1)}</strong> day(s) available
                but requested <strong>{daysRequested.toFixed(1)}</strong> day(s). Reduce the date range.
              </p>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 block">
              Reason for Leave <span className="text-destructive">*</span>
            </label>
            <Textarea
              placeholder="Please provide a detailed reason..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="resize-none text-sm"
            />
          </div>

          {/* Document Upload */}
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              <FileText className="h-3 w-3" /> Attach document
              <span className="text-[10px] text-muted-foreground">(optional)</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleFileChange}
            />
            {!documentFile && request?.document_url && (
              <a
                href={request.document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-1.5 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                <FileText className="h-3.5 w-3.5" />
                Current document attached · View
              </a>
            )}
            {documentFile ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-xs text-primary truncate">{documentFile.name}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {(documentFile.size / 1024).toFixed(0)} KB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setDocumentFile(null)}
                  className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleFileDrop}
                className={`w-full flex items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 transition-colors ${
                  isDragOver
                    ? 'border-primary/60 bg-primary/5'
                    : 'border-border hover:border-primary/40 hover:bg-muted/20'
                }`}
              >
                <UploadCloud className={`h-5 w-5 shrink-0 ${isDragOver ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="text-left">
                  <p className="text-xs font-medium text-foreground">
                    Drop file here or <span className="text-primary">browse</span>
                  </p>
                  <p className="text-[10px] text-muted-foreground">PDF, DOC, JPG, PNG</p>
                </div>
              </button>
            )}
          </div>

          {/* Applied Rules */}
          {selectedRule && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                Applied Rules
              </p>
              <div className="flex flex-wrap gap-1.5">
                {selectedRule.allow_half_day && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[10px] font-semibold px-2.5 py-1">
                    Half-day ✓
                  </span>
                )}
                {!selectedRule.allow_half_day && (
                  <span className="text-xs text-muted-foreground italic">No special rules</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t border-border/60">
          <Button variant="outline" className="flex-1" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Save Changes' : 'Submit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
